import { useState, useEffect } from 'react';
import { X, MessageCircle, Loader2, Send, CheckCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface InboxModalProps {
    isOpen: boolean;
    onClose: () => void;
}

interface ModMessage {
    id: string;
    subject: string;
    message: string;
    status: string;
    has_new_reply?: boolean;
    created_at: string;
}

interface MessageReply {
    id: string;
    message_id: string;
    sender_id: string;
    sender_role: 'user' | 'mod' | 'admin';
    content: string;
    created_at: string;
}

export function InboxModal({ isOpen, onClose }: InboxModalProps) {
    const [messages, setMessages] = useState<ModMessage[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedMessage, setSelectedMessage] = useState<ModMessage | null>(null);
    const [replies, setReplies] = useState<MessageReply[]>([]);
    const [replyText, setReplyText] = useState('');
    const [sending, setSending] = useState(false);

    useEffect(() => {
        if (isOpen) {
            loadMessages();
        }
    }, [isOpen]);

    const loadMessages = async () => {
        setLoading(true);
        try {
            const { data } = await supabase
                .from('mod_messages')
                .select('*')
                .order('created_at', { ascending: false });

            if (data) setMessages(data);
        } catch (err) {
            console.error('Failed to load messages:', err);
        } finally {
            setLoading(false);
        }
    };

    const openThread = async (msg: ModMessage) => {
        setSelectedMessage(msg);
        setReplyText('');

        // Clear new reply flag
        if (msg.has_new_reply) {
            await supabase
                .from('mod_messages')
                .update({ has_new_reply: false })
                .eq('id', msg.id);
        }

        // Load replies
        const { data } = await supabase
            .from('mod_message_replies')
            .select('*')
            .eq('message_id', msg.id)
            .order('created_at', { ascending: true });

        if (data) setReplies(data as MessageReply[]);
    };

    const sendReply = async () => {
        if (!selectedMessage || !replyText.trim()) return;

        setSending(true);
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;

            await supabase
                .from('mod_message_replies')
                .insert({
                    message_id: selectedMessage.id,
                    sender_id: user.id,
                    sender_role: 'user',
                    content: replyText.trim(),
                });

            setReplyText('');
            await openThread(selectedMessage);
        } catch (err) {
            console.error('Failed to send reply:', err);
        } finally {
            setSending(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100] p-4">
            <div className="bg-slate-900 border border-white/10 rounded-xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden">
                {/* Header */}
                <div className="p-4 border-b border-white/10 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                            <MessageCircle className="size-5 text-purple-400" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-white">My Messages</h2>
                            <p className="text-xs text-white/50">Conversations with the mod team</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-white/40 hover:text-white/60">
                        <X className="size-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 flex overflow-hidden">
                    {loading ? (
                        <div className="flex-1 flex items-center justify-center">
                            <Loader2 className="size-8 animate-spin text-purple-400" />
                        </div>
                    ) : messages.length === 0 ? (
                        <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
                            <MessageCircle className="size-12 text-white/20 mb-4" />
                            <p className="text-white/40">No messages yet</p>
                            <p className="text-xs text-white/30 mt-1">
                                Send a message to the mod team from the feedback menu
                            </p>
                        </div>
                    ) : (
                        <>
                            {/* Message list */}
                            <div className={`${selectedMessage ? 'w-1/3 border-r border-white/10' : 'w-full'} overflow-y-auto`}>
                                {messages.map(msg => (
                                    <button
                                        key={msg.id}
                                        onClick={() => openThread(msg)}
                                        className={`w-full text-left p-4 border-b border-white/5 transition-colors ${selectedMessage?.id === msg.id
                                                ? 'bg-purple-500/10'
                                                : 'hover:bg-white/5'
                                            }`}
                                    >
                                        <div className="flex items-center gap-2 mb-1">
                                            {msg.has_new_reply && (
                                                <span className="w-2 h-2 rounded-full bg-purple-400" />
                                            )}
                                            <span className="text-sm text-white font-medium truncate">{msg.subject}</span>
                                        </div>
                                        <p className="text-xs text-white/40 truncate">{msg.message}</p>
                                        <div className="flex items-center gap-2 mt-2">
                                            <span className={`px-2 py-0.5 rounded text-xs ${msg.status === 'replied'
                                                    ? 'bg-purple-500/20 text-purple-400'
                                                    : msg.status === 'unread'
                                                        ? 'bg-amber-500/20 text-amber-400'
                                                        : 'bg-white/10 text-white/40'
                                                }`}>
                                                {msg.status === 'replied' ? 'Mod replied' : msg.status}
                                            </span>
                                            <span className="text-xs text-white/30">
                                                {new Date(msg.created_at).toLocaleDateString()}
                                            </span>
                                        </div>
                                    </button>
                                ))}
                            </div>

                            {/* Thread view */}
                            {selectedMessage && (
                                <div className="flex-1 flex flex-col">
                                    {/* Thread header */}
                                    <div className="p-4 border-b border-white/10">
                                        <h3 className="text-white font-medium">{selectedMessage.subject}</h3>
                                        <p className="text-xs text-white/40">
                                            {new Date(selectedMessage.created_at).toLocaleString()}
                                        </p>
                                    </div>

                                    {/* Messages */}
                                    <div className="flex-1 p-4 space-y-3 overflow-y-auto">
                                        {/* Original message */}
                                        <div className="flex gap-3">
                                            <div className="w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center text-xs text-cyan-400">
                                                You
                                            </div>
                                            <div className="flex-1 bg-cyan-500/10 rounded-lg p-3 border border-cyan-500/20">
                                                <p className="text-sm text-white/80">{selectedMessage.message}</p>
                                            </div>
                                        </div>

                                        {/* Replies */}
                                        {replies.map(reply => (
                                            <div key={reply.id} className={`flex gap-3 ${reply.sender_role === 'user' ? '' : 'flex-row-reverse'}`}>
                                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs ${reply.sender_role === 'admin' ? 'bg-red-500/20 text-red-400' :
                                                        reply.sender_role === 'mod' ? 'bg-purple-500/20 text-purple-400' :
                                                            'bg-cyan-500/20 text-cyan-400'
                                                    }`}>
                                                    {reply.sender_role === 'admin' ? 'A' : reply.sender_role === 'mod' ? 'M' : 'You'}
                                                </div>
                                                <div className={`flex-1 max-w-[80%] rounded-lg p-3 ${reply.sender_role === 'user'
                                                        ? 'bg-cyan-500/10 border border-cyan-500/20'
                                                        : 'bg-purple-500/10 border border-purple-500/20'
                                                    }`}>
                                                    <p className="text-sm text-white/80">{reply.content}</p>
                                                    <p className="text-xs text-white/30 mt-1">
                                                        {reply.sender_role !== 'user' && (
                                                            <span className="text-purple-400">{reply.sender_role} • </span>
                                                        )}
                                                        {new Date(reply.created_at).toLocaleTimeString()}
                                                    </p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Reply input */}
                                    <div className="p-4 border-t border-white/10">
                                        <div className="flex gap-2">
                                            <input
                                                type="text"
                                                value={replyText}
                                                onChange={(e) => setReplyText(e.target.value)}
                                                onKeyDown={(e) => e.key === 'Enter' && sendReply()}
                                                placeholder="Type a reply..."
                                                className="flex-1 px-4 py-2 bg-black/30 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-purple-500/50"
                                            />
                                            <button
                                                onClick={sendReply}
                                                disabled={sending || !replyText.trim()}
                                                className="px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white text-sm rounded-lg transition-colors flex items-center gap-2"
                                            >
                                                {sending ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
